import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Fetches Verticals / Categories / Subcategories from Payload and
// renders three cascading <select>s + a Primary Topic input + a
// compact Structured Entities editor.
//
// Values are STORED AS SLUGS (readable) — the backend resolves
// slug → Payload document id at publish time.
export default function TaxonomyPicker({ form, setForm }) {
  const [verticals, setVerticals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    fetch("/api/cms/api/verticals?limit=100&depth=0")
      .then((r) => r.ok ? r.json() : { docs: [] })
      .then((b) => setVerticals(b.docs || []))
      .catch(() => setVerticals([]));
  }, []);

  useEffect(() => {
    if (!form.vertical) { setCategories([]); return; }
    fetch(`/api/cms/api/taxonomy-categories?limit=200&depth=1&where[vertical.slug][equals]=${encodeURIComponent(form.vertical)}`)
      .then((r) => r.ok ? r.json() : { docs: [] })
      .then((b) => setCategories(b.docs || []))
      .catch(() => setCategories([]));
  }, [form.vertical]);

  useEffect(() => {
    if (!form.category) { setSubcategories([]); return; }
    fetch(`/api/cms/api/taxonomy-subcategories?limit=200&depth=1&where[category.slug][equals]=${encodeURIComponent(form.category)}`)
      .then((r) => r.ok ? r.json() : { docs: [] })
      .then((b) => setSubcategories(b.docs || []))
      .catch(() => setSubcategories([]));
  }, [form.category]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4" data-testid="brief-taxonomy">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SelectField
          label="Vertical" testId="brief-vertical" required
          value={form.vertical}
          onChange={(v) => setForm((f) => ({ ...f, vertical: v, category: "", subcategory: "" }))}
          options={[{ value: "", label: "— pick vertical —" }, ...verticals.map((v) => ({ value: v.slug, label: v.name }))]}
        />
        <SelectField
          label="Category" testId="brief-category"
          value={form.category}
          onChange={(v) => setForm((f) => ({ ...f, category: v, subcategory: "" }))}
          disabled={!form.vertical}
          options={[{ value: "", label: form.vertical ? "— pick category —" : "— pick vertical first —" },
                    ...categories.map((c) => ({ value: c.slug, label: c.name }))]}
        />
        <SelectField
          label="Subcategory" testId="brief-subcategory"
          value={form.subcategory}
          onChange={(v) => set("subcategory", v)}
          disabled={!form.category}
          options={[{ value: "", label: form.category ? "— pick subcategory —" : "— pick category first —" },
                    ...subcategories.map((s) => ({ value: s.slug, label: s.name }))]}
        />
      </div>

      <div>
        <Label className="text-xs font-medium text-neutral-700">Primary topic</Label>
        <Input value={form.primary_topic || ""}
               onChange={(e) => set("primary_topic", e.target.value)}
               placeholder="e.g. solid teak coffee tables"
               data-testid="brief-primary-topic" className="mt-1" />
      </div>

      <StructuredEntitiesEditor
        value={form.structured_entities || {}}
        onChange={(v) => set("structured_entities", v)}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled, testId, required }) {
  return (
    <div>
      <Label className="text-xs font-medium text-neutral-700">
        {label}{required && <span className="text-red-600"> *</span>}
      </Label>
      <select
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testId}
        className="mt-1 w-full h-9 border border-neutral-200 rounded-md px-2 text-sm bg-white disabled:bg-neutral-50 disabled:text-neutral-400"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// Fixed bucket list — matches Taxonomy.ts `structuredEntitiesField`.
const BUCKETS = [
  { key: "brands",            label: "Brands" },
  { key: "materials",         label: "Materials" },
  { key: "products",          label: "Products" },
  { key: "productCategories", label: "Product Categories" },
  { key: "styles",            label: "Styles" },
  { key: "rooms",             label: "Rooms" },
  { key: "countries",         label: "Countries" },
  { key: "cities",            label: "Cities" },
  { key: "certifications",    label: "Certifications" },
  { key: "companies",         label: "Companies" },
  { key: "technologies",      label: "Technologies" },
  { key: "keywords",          label: "Long-tail Keywords" },
];

function StructuredEntitiesEditor({ value, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const summary = useMemo(() => {
    const total = Object.values(value || {})
      .reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0);
    return total;
  }, [value]);

  function setBucket(bucket, str) {
    const parts = str.split(",").map((s) => s.trim()).filter(Boolean);
    onChange({ ...(value || {}), [bucket]: parts });
  }

  return (
    <div className="border border-neutral-200 rounded-md p-3" data-testid="brief-structured-entities">
      <button type="button" onClick={() => setExpanded((v) => !v)}
              className="w-full text-left flex items-center justify-between text-sm font-medium text-neutral-800">
        <span>Structured entities {summary > 0 && <span className="text-neutral-500 font-normal">({summary} total)</span>}</span>
        <span className="text-neutral-400 text-xs">{expanded ? "▲ hide" : "▼ show"}</span>
      </button>
      {expanded && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {BUCKETS.map((b) => (
            <div key={b.key}>
              <Label className="text-xs text-neutral-600">{b.label}</Label>
              <Input
                value={(value?.[b.key] || []).join(", ")}
                onChange={(e) => setBucket(b.key, e.target.value)}
                placeholder="comma, separated, values"
                className="mt-1 text-sm"
                data-testid={`brief-entities-${b.key}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
